import config from "configs/config";
import apiClient from "lib/api-client";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getUserDetail } from "utils/getUserDetailsHelper";

const PermissionsContext = createContext();

const PERMISSION_DEFAULTS = {
  "Vision Web": {
    "Service Requests": [
      { key: "clientName", label: "Client Name", allowed: false },
      { key: "status", label: "Status", allowed: false },
    ],
  },
};

const mergePermissionsWithDefaults = (permissions) => {
  if (!permissions?.rights) return permissions;
  const rights = permissions.rights.map((menuRight) => {
    const menuDefaults = PERMISSION_DEFAULTS[menuRight.menu];
    if (!menuDefaults) return menuRight;
    const subRights = (menuRight.subRights || []).map((subRight) => {
      const subDefaults = menuDefaults[subRight.subMenu];
      if (!subDefaults) return subRight;
      const existingKeys = new Set((subRight.permissions || []).map((p) => p.key));
      const missingPerms = subDefaults.filter((p) => !existingKeys.has(p.key));
      if (!missingPerms.length) return subRight;
      return { ...subRight, permissions: [...(subRight.permissions || []), ...missingPerms] };
    });
    return { ...menuRight, subRights };
  });
  return { ...permissions, rights };
};

export const PermissionsProvider = ({ children }) => {
  const [permissionsState, setPermissions] = useState();
  const userId = getUserDetail("_id") || null;
  const permissionsRef = useRef();

  // useEffect(() => {
  //   apiClient.get(`${config.baseUrl}/api/v1/role/permissions/${userId}`).then((res) => {
  //     setPermissions(res?.data?.role);
  //   });
  // }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const res = await apiClient.get(`${config.baseUrl}/api/v1/role/permissions/${userId}`);
        const newPermissions = mergePermissionsWithDefaults(res?.data?.role);

        if (JSON.stringify(permissionsRef.current) !== JSON.stringify(newPermissions)) {
          permissionsRef.current = newPermissions;
          setPermissions(newPermissions);
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
        if (permissionsRef.current !== null) {
          permissionsRef.current = null;
          setPermissions(null);
        }
      }
    };
    if (userId) {
      fetchPermissions();
    }
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && userId) {
        fetchPermissions();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [userId]);
  const permissions = useMemo(() => permissionsState, [permissionsState]);

  return <PermissionsContext.Provider value={permissions}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => useContext(PermissionsContext);
